import { useState } from 'react';
import { Node, Edge } from 'reactflow';
import { useI18n } from '../../i18n';
import type { TranslationKey } from '../../i18n/translations';

type TFn = (key: TranslationKey, vars?: Record<string, string | number>) => string;
type TemplateCategory = 'Résidentiel' | 'Commercial' | 'Industriel';

const CATEGORY_KEYS: Record<TemplateCategory | 'Tous', TranslationKey> = {
  'Tous': 'arch2_all',
  'Résidentiel': 'arch2_tpl_cat_residential',
  'Commercial': 'arch2_tpl_cat_commercial',
  'Industriel': 'arch2_tpl_cat_industrial',
};

interface InstallationTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: TemplateCategory; // identifiant interne (le libellé affiché est traduit)
  nodes: Omit<Node, 'id'>[];
  edges: Omit<Edge, 'id'>[];
  preview: string;
}

// Modèles construits à l'affichage pour traduire noms, descriptions et noms de zones.
// Les noms de tableaux (« Tableau Principal 2R »…) restent en français : ils sont enregistrés
// comme nom de zone et reconnus par leur préfixe « Tableau » au rechargement.
const buildTemplates = (t: TFn, tp: (base: string, n: number) => string): InstallationTemplate[] => [
  {
    id: 'appartement-t2',
    name: t('arch2_tpl_apartment'),
    description: t('arch2_tpl_apartment_desc'),
    icon: '🏠',
    category: 'Résidentiel',
    preview: '📊→🔲→[💼,🍳,💡]',
    nodes: [
      {
        type: 'linky',
        position: { x: 250, y: 50 },
        data: { label: t('arch2_linky_single'), icon: '📊', power: 9, status: 'online' }
      },
      {
        type: 'tableau',
        position: { x: 250, y: 200 },
        data: { label: 'Tableau Principal 2R', icon: '🔲', status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 100, y: 350 },
        data: { label: t('arch2_tpl_z_living'), icon: '💼', power: 2.5, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 250, y: 350 },
        data: { label: t('arch2_tpl_z_kitchen'), icon: '🍳', power: 3.5, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 400, y: 350 },
        data: { label: t('arch2_tpl_z_bedroom'), icon: '💡', power: 1.5, status: 'online' }
      },
    ],
    edges: []
  },
  {
    id: 'maison-individuelle',
    name: t('arch2_tpl_house'),
    description: t('arch2_tpl_house_desc'),
    icon: '🏡',
    category: 'Résidentiel',
    preview: `📊→🔲→[${tp('arch3_tpl_preview_zones', 6)}]`,
    nodes: [
      {
        type: 'linky',
        position: { x: 300, y: 50 },
        data: { label: t('arch2_linky_single'), icon: '📊', power: 12, status: 'online' }
      },
      {
        type: 'tableau',
        position: { x: 200, y: 200 },
        data: { label: 'Tableau Principal 3R', icon: '🔲', status: 'online' }
      },
      {
        type: 'tableau',
        position: { x: 400, y: 200 },
        data: { label: 'Tableau Secondaire', icon: '📦', status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 50, y: 350 },
        data: { label: t('arch2_tpl_z_garage'), icon: '🚗', power: 2, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 150, y: 350 },
        data: { label: t('arch2_tpl_z_kitchen'), icon: '🍳', power: 4, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 250, y: 350 },
        data: { label: t('arch2_tpl_z_living'), icon: '💼', power: 3, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 350, y: 350 },
        data: { label: t('arch2_tpl_z_bedrooms'), icon: '🛏️', power: 2.5, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 450, y: 350 },
        data: { label: t('arch2_tpl_z_bathroom'), icon: '🚿', power: 3.5, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 550, y: 350 },
        data: { label: t('arch2_tpl_z_outdoor'), icon: '🌳', power: 1.5, status: 'online' }
      },
    ],
    edges: []
  },
  {
    id: 'bureau-openspace',
    name: t('arch2_tpl_office'),
    description: t('arch2_tpl_office_desc'),
    icon: '🏢',
    category: 'Commercial',
    preview: '📊→🔲→[💼,💻,❄️,💡]',
    nodes: [
      {
        type: 'linky',
        position: { x: 300, y: 50 },
        data: { label: t('arch2_linky_three'), icon: '📊', power: 36, status: 'online' }
      },
      {
        type: 'tableau',
        position: { x: 300, y: 200 },
        data: { label: 'Tableau Tertiaire 4R', icon: '🔲', status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 150, y: 350 },
        data: { label: t('arch2_tpl_z_offices'), icon: '💼', power: 8, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 250, y: 350 },
        data: { label: t('arch2_tpl_z_server_room'), icon: '💻', power: 12, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 350, y: 350 },
        data: { label: t('arch2_tpl_z_hvac'), icon: '❄️', power: 10, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 450, y: 350 },
        data: { label: t('arch2_tpl_z_lighting'), icon: '💡', power: 4, status: 'online' }
      },
    ],
    edges: []
  },
  {
    id: 'commerce',
    name: t('arch2_tpl_shop'),
    description: t('arch2_tpl_shop_desc'),
    icon: '🏪',
    category: 'Commercial',
    preview: `📊→🔲→[${tp('arch3_tpl_preview_zones', 5)}]`,
    nodes: [
      {
        type: 'linky',
        position: { x: 300, y: 50 },
        data: { label: t('arch2_linky_three'), icon: '📊', power: 24, status: 'online' }
      },
      {
        type: 'tableau',
        position: { x: 300, y: 200 },
        data: { label: 'Tableau Commerce 3R', icon: '🔲', status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 100, y: 350 },
        data: { label: t('arch2_tpl_z_window'), icon: '✨', power: 2, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 200, y: 350 },
        data: { label: t('arch2_tpl_z_sales'), icon: '🛒', power: 5, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 300, y: 350 },
        data: { label: t('arch2_tpl_z_storeroom'), icon: '📦', power: 3, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 400, y: 350 },
        data: { label: t('arch2_tpl_z_ac'), icon: '❄️', power: 6, status: 'online' }
      },
      {
        type: 'zone',
        position: { x: 500, y: 350 },
        data: { label: t('arch2_tpl_z_lighting'), icon: '💡', power: 4, status: 'online' }
      },
    ],
    edges: []
  },
];

interface TemplatesSelectorProps {
  onSelectTemplate: (nodes: Node[], edges: Edge[]) => void;
  onClose: () => void;
}

export function TemplatesSelector({ onSelectTemplate, onClose }: TemplatesSelectorProps) {
  const { t, tp } = useI18n();
  const [selectedCategory, setSelectedCategory] = useState<'Tous' | TemplateCategory>('Tous');

  const filteredTemplates = buildTemplates(t, tp).filter(
    tpl => selectedCategory === 'Tous' || tpl.category === selectedCategory
  );

  const handleSelectTemplate = (template: InstallationTemplate) => {
    // Generate unique IDs for nodes and edges
    const nodes: Node[] = template.nodes.map((node, idx) => ({
      ...node,
      id: `${template.id}-node-${idx}`,
    }));

    const edges: Edge[] = [];
    // Auto-connect: compteur → tableau → zones
    const compteur = nodes.find(n => n.type === 'linky');
    const tableaux = nodes.filter(n => n.type === 'tableau');
    const zones = nodes.filter(n => n.type === 'zone');

    if (compteur && tableaux.length > 0) {
      tableaux.forEach(tableau => {
        edges.push({
          id: `edge-${compteur.id}-${tableau.id}`,
          source: compteur.id,
          target: tableau.id,
          type: 'smoothstep',
          style: { stroke: '#0891b2', strokeWidth: 2 },
        });
      });
    }

    if (tableaux.length > 0 && zones.length > 0) {
      const tableau = tableaux[0];
      zones.forEach(zone => {
        edges.push({
          id: `edge-${tableau.id}-${zone.id}`,
          source: tableau.id,
          target: zone.id,
          type: 'smoothstep',
          style: { stroke: '#0891b2', strokeWidth: 2 },
        });
      });
    }

    onSelectTemplate(nodes, edges);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <span className="text-3xl">📋</span>
                {t('arch2_tpl_title')}
              </h2>
              <p className="text-cyan-100 mt-1">{t('arch2_tpl_subtitle')}</p>
            </div>
            <button
              onClick={onClose}
              aria-label={t('arch2_close')}
              title={t('arch2_close')}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center justify-center text-xl"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="p-4 border-b flex gap-2">
          {(['Tous', 'Résidentiel', 'Commercial', 'Industriel'] as const).map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === cat
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t(CATEGORY_KEYS[cat])}
            </button>
          ))}
        </div>

        {/* Templates Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredTemplates.map(template => (
              <div
                key={template.id}
                className="group border-2 border-gray-200 hover:border-cyan-500 rounded-xl overflow-hidden transition-all cursor-pointer hover:shadow-xl"
                onClick={() => handleSelectTemplate(template)}
              >
                {/* Template Preview */}
                <div className="bg-gradient-to-br from-slate-100 to-slate-200 p-8 flex items-center justify-center h-40">
                  <div className="text-center">
                    <div className="text-5xl mb-2">{template.icon}</div>
                    <div className="text-xs text-gray-600 font-mono bg-white/80 px-3 py-1 rounded">
                      {template.preview}
                    </div>
                  </div>
                </div>

                {/* Template Info */}
                <div className="p-5 bg-white">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-lg text-gray-900">{template.name}</h3>
                    <span className="px-2 py-1 bg-cyan-100 text-cyan-800 text-xs rounded font-medium">
                      {t(CATEGORY_KEYS[template.category])}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{template.description}</p>
                  
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex gap-4 text-xs text-gray-500">
                      <span>📊 {tp('arch2_tpl_meters', template.nodes.filter(n => n.type === 'linky').length)}</span>
                      <span>🔲 {tp('arch2_tpl_panels', template.nodes.filter(n => n.type === 'tableau').length)}</span>
                      <span>⚡ {tp('arch2_tpl_zones', template.nodes.filter(n => n.type === 'zone').length)}</span>
                    </div>
                    <button className="px-4 py-2 bg-cyan-500 text-white rounded-lg text-sm font-medium group-hover:bg-cyan-600 transition-colors">
                      {t('arch2_tpl_use')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <p className="text-4xl mb-3">🔍</p>
              <p>{t('arch2_tpl_none')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="text-lg">💡</span>
            <span>{t('arch2_tpl_footer')}</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            {t('arch2_cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
